import React from 'react';
import { Box } from '@mui/material';
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  closestCenter,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Comic, ComicGroup, LibraryShelf, LibraryTile, groupDragId } from '../types';
import { useDragSensors } from '../../Hub/components/Links/drag';
import GroupShelf from './GroupShelf';
import { TileProgress } from './ShelfTile';

interface LibraryViewProps {
  shelves: LibraryShelf[];
  isReadOnly: boolean;
  isMobile: boolean;
  canDrag: boolean;
  tileCover: (tile: LibraryTile) => string | undefined;
  tileProgress: (tile: LibraryTile) => TileProgress | undefined;
  onOpenTile: (tile: LibraryTile) => void;
  onTileMenu: (e: React.MouseEvent<HTMLButtonElement>, tile: LibraryTile) => void;
  onToggleRead: (comic: Comic) => void;
  onGroupMenu: (e: React.MouseEvent<HTMLButtonElement>, group: ComicGroup) => void;
  onDragEnd: (event: DragEndEvent) => void;
  layout?: 'auto' | 'grid';
  hideHeadings?: boolean;
  // Each shelf's own page, if it has one
  sectionHref?: (shelf: LibraryShelf) => string | undefined;
  onOpenSection?: () => void;
}

// Groups only land on groups, and covers only on covers in the same shelf.
const sameContainer: CollisionDetection = (args) => {
  const container = args.active.data.current?.container;
  return closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      (entry) => entry.data.current?.container === container
    ),
  });
};

const LibraryView = ({
  shelves,
  isReadOnly,
  isMobile,
  canDrag,
  tileCover,
  tileProgress,
  onOpenTile,
  onTileMenu,
  onToggleRead,
  onGroupMenu,
  onDragEnd,
  layout = 'auto',
  hideHeadings = false,
  sectionHref,
  onOpenSection,
}: LibraryViewProps) => {
  const sensors = useDragSensors();

  const groupIds = shelves
    .filter((shelf) => shelf.group)
    .map((shelf) => groupDragId((shelf.group as ComicGroup).id));

  return (
    <DndContext sensors={sensors} collisionDetection={sameContainer} onDragEnd={onDragEnd}>
      <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {shelves.map((shelf) => (
            <GroupShelf
              key={shelf.key}
              shelf={shelf}
              isReadOnly={isReadOnly}
              isMobile={isMobile}
              canDrag={canDrag}
              tileCover={tileCover}
              tileProgress={tileProgress}
              onOpenTile={onOpenTile}
              onTileMenu={onTileMenu}
              onToggleRead={onToggleRead}
              onGroupMenu={onGroupMenu}
              layout={layout}
              hideHeading={hideHeadings}
              sectionHref={sectionHref?.(shelf)}
              onOpenSection={onOpenSection}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default LibraryView;
